import { MoneyBillIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import React from 'react'
import BentoTile from './BentoTile'
import { sectionTeaserCopy } from './copy'
import { MoneySection, TileState } from './core'
import EmptyState from './EmptyState'
import {
	MoneyBody,
	MoneyDivider,
	MoneyStats,
	StatBlock,
	StatDivider,
	StatSub,
	StatValue,
	TeaserNote,
} from './MoneyTile.styles'

/** The expense total + "N expenses" subtitle. */
const ExpenseStat: React.FC<{ section: MoneySection }> = ({ section }) => (
	<StatBlock>
		<StatValue>{section.expenseTotal}</StatValue>
		<StatSub>{countLabel(section.expenseCount, 'expense')}</StatSub>
	</StatBlock>
)

/** The pay-up total + "N pay ups" subtitle. */
const PayUpStat: React.FC<{ section: MoneySection }> = ({ section }) => (
	<StatBlock>
		<StatValue>{section.payUpTotal}</StatValue>
		<StatSub>{countLabel(section.payUpCount, 'pay up')}</StatSub>
	</StatBlock>
)

interface MoneyTileProps {
	section: MoneySection
	/** Resolved tile state. Member mode always shows full detail — share flags don't apply here. */
	state: TileState
	/** Navigate to the balances page. */
	onNavigate: () => void
	/** Render a skeleton while the expenses/pay-ups queries load. */
	loading?: boolean
}

/**
 * The expenses & pay ups tile: total spend and total settled, both in the trip
 * default currency. The public teaser shows only the anonymous expense total —
 * pay-up figures never render there.
 */
const MoneyTile: React.FC<MoneyTileProps> = ({ section, state, onNavigate, loading }) => {
	const icon = <MoneyBillIcon sx={{ fontSize: 16 }} />
	const { expenseCount, hasExpenses, payUpCount } = section
	const totalCount = expenseCount + payUpCount

	if (state === 'teaser') {
		return (
			<BentoTile
				icon={icon}
				label="Expenses & pay ups"
				count={totalCount > 0 ? totalCount : undefined}
				ariaLabel="Expense details hidden on this shared link"
				colSpan={6}
				loading={loading}
			>
				<MoneyBody>
					<ExpenseStat section={section} />
					<MoneyDivider />
					<TeaserNote>{sectionTeaserCopy('expenses')}</TeaserNote>
				</MoneyBody>
			</BentoTile>
		)
	}

	const body =
		state === 'detail' ? (
			<MoneyStats>
				{hasExpenses && <ExpenseStat section={section} />}
				{hasExpenses && payUpCount > 0 && <StatDivider aria-hidden="true" />}
				{payUpCount > 0 && <PayUpStat section={section} />}
			</MoneyStats>
		) : (
			<EmptyState
				icon={<MoneyBillIcon sx={{ fontSize: 32 }} />}
				text="No expenses yet — track group spending and who owes whom."
			/>
		)

	return (
		<BentoTile
			icon={icon}
			label="Expenses & pay ups"
			count={totalCount > 0 ? totalCount : undefined}
			footer="See all expenses & balances"
			ariaLabel="View expenses and balances"
			colSpan={6}
			onClick={onNavigate}
			loading={loading}
		>
			{body}
		</BentoTile>
	)
}

export default MoneyTile
